import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import RequestTutorModal from "../requestTutor/RequestTutorModal";
import "./HeroBanner.css";

// Each suggestion maps to the exact backend params the endpoint expects:
// - category → matches selectedCategories (e.g. "WAEC/NECO", "JAMB", "Adult Education")
// - subject  → matches courses inside categoryDetails (e.g. "Science", "Arts", "Commercial")
const SUGGESTIONS = [
  {
    label: "Adult Education Tutor Near Me",
    params: { category: "Adult Education" },
  },
  {
    label: "WAEC Science Tutor Near Me",
    params: { category: "WAEC/NECO", subject: "Science" },
  },
  {
    label: "WAEC Arts Tutor Near Me",
    params: { category: "WAEC/NECO", subject: "Arts" },
  },
  {
    label: "WAEC Commercial Tutor Near Me",
    params: { category: "WAEC/NECO", subject: "Commercial" },
  },
  {
    label: "JAMB Science Tutor Near Me",
    params: { category: "JAMB", subject: "Science" },
  },
  {
    label: "JAMB Arts Tutor Near Me",
    params: { category: "JAMB", subject: "Arts" },
  },
  {
    label: "JAMB Commercial Tutor Near Me",
    params: { category: "JAMB", subject: "Social Science" },
  },
];

const HeroBanner = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showRequestTutorModal, setShowRequestTutorModal] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Free-text search — passes value as `q` param
  const handleSearch = (e) => {
    e.preventDefault();
    const trimmedSearch = searchInput.trim();
    if (!trimmedSearch) return;
    setShowSuggestions(false);
    const params = new URLSearchParams();
    params.set("q", trimmedSearch);
    navigate({ pathname: "/search", search: params.toString() });
  };

  // Suggestion click — passes structured params the backend actually understands
  const handleSuggestionClick = (suggestion) => {
    setSearchInput(suggestion.label);
    setShowSuggestions(false);
    const params = new URLSearchParams();
    Object.entries(suggestion.params).forEach(([key, value]) => {
      params.set(key, value);
    });
    navigate({ pathname: "/search", search: params.toString() });
  };

  return (
    <section className="hero-banner">
      <div className="hero-container">
        <h1 className="hero-title">
          TutorsLinks… <br className="mobile-break" />
          Find Adult Literacy, WAEC and JAMB Tutors Near You!
        </h1>

        <form className="search-container" onSubmit={handleSearch}>
          <div className="search-bar-wrapper" ref={wrapperRef}>
            <div className="search-bar">
              <img
                src="/images/search.png"
                alt=""
                aria-hidden="true"
                className="search-logo"
                width={20}
                height={20}
                fetchPriority="high"
                decoding="sync"
              />
              <input
                type="text"
                placeholder="Search program/course, tutor name, training center, or location (e.g. Lagos)"
                className="search-input full-width"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                autoComplete="off"
              />
              <button type="submit" className="search-btn">
                <img
                  src="/images/search-icon2.png"
                  alt=""
                  aria-hidden="true"
                  className="search-icon2"
                  width={20}
                  height={20}
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                />
                <span className="search-text">Search</span>
              </button>
            </div>

            {showSuggestions && (
              <ul className="search-suggestions" role="listbox">
                {SUGGESTIONS.map((suggestion) => (
                  <li
                    key={suggestion.label}
                    className="search-suggestion-item"
                    role="option"
                    onMouseDown={() => handleSuggestionClick(suggestion)}
                  >
                    <svg
                      className="suggestion-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <span>{suggestion.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </form>

        <div className="hero-request-tutor-row">
          <span className="hero-request-tutor-text">
            Not sure what to search for?
          </span>
          <button
            type="button"
            className="hero-request-tutor-btn"
            onClick={() => setShowRequestTutorModal(true)}
          >
            ✋ Request a Tutor
          </button>
        </div>

        <div className="video-section">
          <p className="video-caption">
            Search tutors with keywords like JAMB, Adult Education, Maths
            Teacher, etc
          </p>
        </div>
      </div>

      {showRequestTutorModal && (
        <RequestTutorModal onClose={() => setShowRequestTutorModal(false)} />
      )}
    </section>
  );
};

export default HeroBanner;
